import React, { useCallback, useEffect, useState } from 'react';

import { useBranchContext } from 'state/branch';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import { MParticleAdapter } from 'state/mParticle/mparticle-adapter';
import { getConfigValue } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import LogRocketHelper from 'utils/logrocket';

import useOneTrustSdk from './use-one-trust-sdk';

type ConsentGroups = {
  C0001: number;
  C0002: number;
  C0003: number;
  C0004: number;
};

enum OneTrustGlobalConsentState {
  OPT_OUT = 'Opt-Out',
  OPT_IN = 'Opt-In',
}

export const OneTrustCookieBanner = () => {
  const enableLogRocket = useFlag(LaunchDarklyFlag.ENABLE_LOGROCKET);
  const { init: initMParticle } = useMParticleContext();
  const { initBranch } = useBranchContext();
  const lrInstance = LogRocketHelper.getInstance().logrocket;
  const { loaded: otSdkLoaded } = useOneTrustSdk();

  const [servicesLoaded, setServiceLoaded] = useState(false);
  const [otConsentGroups, setConsentGroups] = useState({} as ConsentGroups);
  const [consentGroupsSetInLocalStorage, setConsentGroupsSetInLocalStorage] = useState(false);
  // Fucntion to initialize 3rd party services
  const initServicesWithCookies = useCallback(() => {
    if (!window.mParticle) {
      initMParticle();
    }
    if (enableLogRocket && !lrInstance) {
      LogRocketHelper.init();
    }
    if (!window.branch) {
      initBranch();
    }
  }, [enableLogRocket, initBranch, initMParticle, lrInstance]);

  // loads 3rd party services once OneTrust sdk is loaded
  useEffect(() => {
    if (otConsentGroups && otSdkLoaded && !servicesLoaded) {
      initServicesWithCookies();
    }
  }, [initServicesWithCookies, otConsentGroups, otSdkLoaded, servicesLoaded]);

  const servicesInitCheck = useCallback(() => {
    const areServicesInitialized = !!window.mParticle && !!window.branch && !!window.appboy;
    if (areServicesInitialized) {
      setServiceLoaded(true);
    }
    return areServicesInitialized;
  }, []);

  // Sets a interval to check if all 3rd party services are initialized
  useEffect(() => {
    const id = setInterval(servicesInitCheck, 1000);
    if (servicesLoaded) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [servicesInitCheck, servicesLoaded]);

  const getConsentGroups = (consentState: String[]) => {
    const groups: ConsentGroups = {
      C0001: 0,
      C0002: 0,
      C0003: 0,
      C0004: 0,
    };

    return Object.keys(groups).reduce((accumulator, group) => {
      if (consentState.includes(group)) {
        accumulator[group] = 1;
      }
      return accumulator;
    }, groups);
  };

  // Registers onetrust consent change handler
  useEffect(() => {
    const id = setInterval(() => {
      if (window?.OneTrust && otSdkLoaded) {
        window.OneTrust.OnConsentChanged((event: any) => {
          const consentState = event?.detail ?? [];
          const consentGroups = getConsentGroups(consentState);
          setConsentGroups(consentGroups);
          LocalStorage.setItem(StorageKeys.ONETRUST_COOKIE_CONSENT_GROUPS, consentGroups);
        });
        setConsentGroupsSetInLocalStorage(true);
      }
    }, 1000);
    if (consentGroupsSetInLocalStorage) {
      clearInterval(id);
    }
    return () => clearInterval(id);
  }, [otSdkLoaded, consentGroupsSetInLocalStorage]);

  // Pull the consent groups out of local storage if it's already populated.
  // This will trigger the hook below, and disable/enable any global services based on
  // the previously chosen settings.
  useEffect(() => {
    const existingConsentGroups = LocalStorage.getItem(StorageKeys.ONETRUST_COOKIE_CONSENT_GROUPS);
    if (existingConsentGroups) {
      setConsentGroups(existingConsentGroups);
    }
  }, []);

  // Switches off tracking for each service if data tracking is opted out
  useEffect(() => {
    const {
      globalConsentState,
      consentCategoryMParticle,
      consentCategoryBraze,
      consentCategoryBranch,
    } = getConfigValue('oneTrust').cookieCompliance;

    // reads consent state for each consent category
    const mparticleConsentState = otConsentGroups[consentCategoryMParticle];
    const brazeConsentState = otConsentGroups[consentCategoryBraze];
    const branchConsentState = otConsentGroups[consentCategoryBranch];

    // if consent state is opted out for respective service, data tracking is opted out
    if (servicesLoaded) {
      const isOptOutState = globalConsentState === OneTrustGlobalConsentState.OPT_OUT;
      if (mparticleConsentState === 0 || isOptOutState) {
        MParticleAdapter.setOptOut(true);
      }
      if (brazeConsentState === 0 || isOptOutState) {
        window.appboy.stopWebTracking();
      }
      if (branchConsentState === 0 || isOptOutState) {
        window.branch.disableTracking(true);
      }
      if (mparticleConsentState === 1) {
        MParticleAdapter.setOptOut(false);
      }
      if (brazeConsentState === 1) {
        window.appboy.resumeWebTracking();
      }
      if (branchConsentState === 1) {
        window.branch.disableTracking(false);
      }
    }
  }, [otConsentGroups, servicesLoaded]);

  return <div data-testid="one-trust-cookie-banner" />;
};
